import { type ReactNode, type RefObject, useEffect, useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import { useGetBusiness } from "@/business/query";
import { EDITOR_POLL_INTERVAL } from "@/payroll/editor/constants";
import { EditorConversation } from "@/payroll/editor/editor-conversation";
import { usePayrollEditor } from "@/payroll/editor/query";
import { RulesReview } from "@/payroll/editor/rules-review";
import {
  editorError,
  errorStatus,
  isAccessError,
} from "@/payroll/editor/state";
import { EditorUnavailableState } from "./editor-unavailable-state";
import { useResizableColumn } from "./use-resizable-column";
import { WorkspaceSkeleton } from "./workspace-skeleton";

const Workbench = ({
  children,
  containerRef,
}: {
  children: ReactNode;
  containerRef: RefObject<HTMLDivElement | null>;
}) => (
  <Stack
    ref={containerRef}
    className="@container/editor quiet-scrollbar min-h-0 min-w-0 flex-1 overflow-hidden text-sm text-ink"
  >
    {children}
  </Stack>
);

export const EditorWorkspace = ({
  businessId,
  userId,
}: {
  businessId: string;
  userId: string;
}) => {
  // A full workbook needs the window. Expanding keeps the conversation as a
  // narrow rail so the owner can still ask about what they are looking at.
  const [tab, setTab] = useState("changes");
  const [periodStart, setPeriodStart] = useState<string>();
  const { width, dragging, separatorProps, containerRef, isMeasured } =
    useResizableColumn(`payroll-editor:${businessId}:column`, 480);
  const business = useGetBusiness(businessId, undefined, {
    refetchInterval: EDITOR_POLL_INTERVAL.idle,
  });
  const allowed = business.data?.can_edit_payroll_rules === true;
  const editor = usePayrollEditor(businessId, allowed);
  const { workspaceId } = editor;
  const workspace = editor.messages.data?.workspace ?? editor.current.data;
  const accessError = [
    editor.messages.error,
    editor.current.error,
    editor.documents.error,
    editor.send.error,
    editor.upload.error,
    editor.remove.error,
    editor.open.error,
    editor.publish.error,
    editor.discard.error,
    editor.cancel.error,
  ].find(isAccessError);
  // There is no landing screen and no workspace in the URL. A reader who opens
  // Payroll rules wants the workspace, and the workspace already renders the
  // current rules on its Rules tab, which reads as the plan when nothing has
  // moved. The landing was a second way to read the same document with a door
  // on it, and once you went through the door there was no way back, which is
  // the proof that nothing needed it.
  //
  // Opening is idempotent on the API: an existing draft and its thread are
  // reused, so this creates at most one draft per business, and a draft
  // identical to the published rules reports `has_changes: false`.
  const needsWorkspace =
    allowed && !accessError && !!editor.current.data && !workspaceId;
  useEffect(() => {
    if (!needsWorkspace || editor.open.isPending || editor.open.isError) return;
    editor.open.mutate();
  }, [needsWorkspace, editor.open]);

  // Show controls only after their resize limits are measured.
  if (business.isPending || !isMeasured)
    return (
      <Workbench containerRef={containerRef}>
        <WorkspaceSkeleton width={width} />
      </Workbench>
    );
  if (business.isError)
    return (
      <Workbench containerRef={containerRef}>
        <EditorUnavailableState
          reason="load-failed"
          error={business.error}
          onRetry={() => void business.refetch()}
        />
      </Workbench>
    );
  if (!allowed || accessError)
    return (
      <Workbench containerRef={containerRef}>
        <EditorUnavailableState reason="no-access" error={accessError} />
      </Workbench>
    );

  const readError = editor.messages.error ?? editor.current.error;
  if (!workspace && !readError)
    return (
      <Workbench containerRef={containerRef}>
        <WorkspaceSkeleton width={width} />
      </Workbench>
    );
  if (!workspace || errorStatus(readError) === 404)
    return (
      <Workbench containerRef={containerRef}>
        <EditorUnavailableState
          reason="load-failed"
          error={readError}
          onRetry={() => void editor.current.refetch()}
        />
      </Workbench>
    );
  return (
    <Workbench containerRef={containerRef}>
      {!workspaceId ? (
        editor.open.isError ? (
          <Stack
            align="start"
            gap={4}
            justify="center"
            className="min-h-0 flex-1"
          >
            <div className="space-y-3 p-6" role="alert">
              <p>{editorError(editor.open.error)}</p>
              <Button size="xs" onClick={() => editor.open.reset()}>
                Try again
              </Button>
            </div>
          </Stack>
        ) : (
          <WorkspaceSkeleton width={width} label="Opening payroll rules" />
        )
      ) : (
        <div
          // The split stays within the page, including when navigation changes width.
          style={
            {
              "--split": `${width}px 1px minmax(0, 1fr)`,
            } as React.CSSProperties
          }
          className={cn(
            "grid min-h-0 w-full flex-1 grid-cols-1 grid-rows-[minmax(0,1fr)_minmax(0,1fr)] bg-surface-raised",
            "@min-[741px]/editor:grid-cols-(--split) @min-[741px]/editor:grid-rows-1",
          )}
        >
          <EditorConversation
            key={`conversation:${workspaceId}`}
            editor={editor}
            workspace={workspace}
            businessId={businessId}
            workspaceId={workspaceId}
            storageKey={`payroll-editor:${userId}:${businessId}:${workspaceId}:message`}
          />
          <div
            {...separatorProps}
            // Above everything beside it. The hover band is 13px wide and runs
            // the full height, so wherever a neighbour painted over it the
            // band broke: it showed against the conversation and the artifact,
            // disappeared behind the headers and the roster's pinned columns,
            // and read as three disconnected pieces of one line.
            className="group relative z-30 hidden w-px cursor-col-resize bg-line select-none focus-visible:outline-hidden @min-[741px]/editor:block"
          >
            {/* A 1px line is the right weight and the wrong hit area.

                Neutral, not the interactive green. The band runs the whole
                height of the split, so hovering anywhere on it painted a green
                stripe up through the white tab bar and the control row, which
                read as something happening in the header rather than as a
                column about to be resized. */}
            <span
              aria-hidden
              className={cn(
                "absolute inset-y-0 -right-1.5 -left-1.5 transition-colors",
                // Hover is quieter than the two states that mean something
                // a 13px band running the full height of the split it read as
                // a bar appearing beside the conversation rather than as a
                // line you could pick up. Keyboard focus and an actual drag
                // stay at full strength: those are states the reader put the
                // separator into, not ones the pointer passed through.
                "group-hover:bg-neutral-hovered/30 group-focus-visible:bg-neutral",
                { "bg-neutral": dragging },
              )}
            />
          </div>
          <RulesReview
            key={`review:${workspaceId}`}
            workspace={workspace}
            businessId={businessId}
            workspaceId={workspaceId}
            tab={tab}
            onTabChange={setTab}
            periodStart={periodStart}
            onPeriodChange={setPeriodStart}
            publish={editor.publish}
            discard={editor.discard}
            busy={editor.messages.data?.isActive ?? false}
          />
        </div>
      )}
    </Workbench>
  );
};
